import { GridCellKind } from '@glideapps/glide-data-grid';
import type { CustomCell, CustomRenderer } from '@glideapps/glide-data-grid';
import { type Chip, paintChips } from './chipPainting.js';
import { PicklistEditor, type PicklistOption } from './PicklistEditor.js';

export interface MultiPicklistData {
  readonly kind: 'multiPicklist';
  readonly selected: readonly string[];
  readonly options: readonly PicklistOption[];
  /**
   * Chips shown after the chosen ones and belonging to nobody: values a feed
   * observed rather than values a person picked. The editor never sees them,
   * so they cannot be toggled and are never part of what a commit writes.
   */
  readonly observed?: readonly Chip[] | undefined;
}

export type MultiPicklistCell = CustomCell<MultiPicklistData>;

function labelsOf(data: MultiPicklistData): string[] {
  const byId = new Map(data.options.map((option) => [option.id, option.label]));
  return data.selected.map((id) => byId.get(id) ?? id);
}

export const multiPicklistRenderer: CustomRenderer<MultiPicklistCell> = {
  kind: GridCellKind.Custom,
  isMatch: (cell: CustomCell): cell is MultiPicklistCell =>
    'kind' in cell.data && cell.data.kind === 'multiPicklist',
  draw: (args, cell) => {
    paintChips(args, [
      ...labelsOf(cell.data).map((label) => ({ label, tone: 'neutral' as const })),
      ...(cell.data.observed ?? []),
    ]);
  },
  provideEditor: () => ({
    disablePadding: true,
    editor: (props) => (
      <PicklistEditor
        multiple
        options={props.value.data.options}
        selected={props.value.data.selected}
        onToggle={(id) => {
          const selected = props.value.data.selected.includes(id)
            ? props.value.data.selected.filter((current) => current !== id)
            : [...props.value.data.selected, id];
          props.onChange({ ...props.value, data: { ...props.value.data, selected } });
        }}
        onCommit={() => props.onFinishedEditing(props.value)}
        onRevert={() => props.onFinishedEditing(undefined)}
      />
    ),
  }),
};

export function multiPicklistCell(
  selected: readonly string[],
  options: readonly PicklistOption[],
  observed: readonly Chip[] = [],
): MultiPicklistCell {
  const data: MultiPicklistData = { kind: 'multiPicklist', selected, options, observed };
  return {
    kind: GridCellKind.Custom,
    allowOverlay: true,
    copyData: labelsOf(data).join(', '),
    data,
  };
}
